import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link as MLink,
} from '@mui/material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ClientEstimate {
  id: number;
  case_id: number;
  client_firm_id: number;
  billing_type: string;
  rate: number;
  quantity: number;
  total_amount: number;
  description: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  created_at: string;
  rejection_reason: string | null;
  case: {
    id: number;
    title: string;
    subtitle?: string;
    project: {
      id: number;
      name: string;
    };
  };
  client_firm?: {
    id: number;
    name: string;
  };
  created_by_user?: {
    id: number;
    name: string;
  };
}

const FirmClientEstimates: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [estimates, setEstimates] = useState<ClientEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [firmName, setFirmName] = useState('');
  
  const [rejectingEstimate, setRejectingEstimate] = useState<ClientEstimate | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  const isClientAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((role) => 
      ['firm_admin', 'firm_main', 'client_admin', 'client_manager'].includes(role.slug)
    );
  };

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  useEffect(() => {
    loadFirmName();
    loadEstimates();
  }, [firmId]);

  const loadFirmName = async () => {
    try {
      const response = await api.get(`/firms/${firmId}`);
      if (response.data.success) {
        setFirmName(response.data.data.name);
      }
    } catch (err) {
      console.error('Failed to load firm name:', err);
    }
  };

  const loadEstimates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch pending client estimates for this firm
      const response = await api.get('/client-estimates/pending', {
        params: { firm_id: firmId }
      });
      
      if (response.data.success) {
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (estimate: ClientEstimate) => {
    if (!window.confirm(`Approve estimate for $${Number(estimate.total_amount).toFixed(2)}?`)) {
      return;
    }

    try {
      setProcessing(true);
      const response = await api.put(`/client-estimates/${estimate.id}/status`, {
        action: 'approve'
      });

      if (response.data.success) {
        setSuccess('Estimate approved successfully');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve estimate');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (estimate: ClientEstimate) => {
    setRejectingEstimate(estimate);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectingEstimate) return;

    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      const response = await api.put(`/client-estimates/${rejectingEstimate.id}/status`, {
        action: 'reject',
        rejection_reason: rejectionReason
      });

      if (response.data.success) {
        setSuccess('Estimate rejected successfully');
        setShowRejectDialog(false);
        setRejectingEstimate(null);
        setRejectionReason('');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject estimate');
    } finally {
      setProcessing(false);
    }
  };

  const getBillingTypeLabel = (type: string): string => {
    switch (type) {
      case 'page_based':
        return 'Page Based';
      case 'time_based':
        return 'Time Based';
      case 'case_based':
        return 'Case Based';
      default:
        return type;
    }
  };

  if (!isClientAdmin() && !isPmpAdminOrSiteAdmin()) {
    return (
      <Box>
        <Alert severity="error">
          You don't have permission to access this page. Only Client Admin and Client Manager roles can approve estimates.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MLink
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/firms');
          }}
          sx={{ textDecoration: 'none' }}
        >
          Firms
        </MLink>
        <MLink
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/firms/${firmId}`);
          }}
          sx={{ textDecoration: 'none' }}
        >
          {firmName || `Firm #${firmId}`}
        </MLink>
        <Typography color="text.primary">Pending Estimates</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Pending Client Estimates</Typography>
        <Typography variant="body2" color="text.secondary">
          {estimates.length} estimate{estimates.length !== 1 ? 's' : ''} awaiting approval
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {estimates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No pending estimates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All estimates have been reviewed
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Case</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Billing Type</strong></TableCell>
                <TableCell><strong>Quantity</strong></TableCell>
                <TableCell><strong>Rate</strong></TableCell>
                <TableCell><strong>Total Amount</strong></TableCell>
                <TableCell><strong>Created By</strong></TableCell>
                <TableCell><strong>Submitted On</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimates.map((estimate) => (
                <TableRow key={estimate.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {estimate.case.title}
                      </Typography>
                      {estimate.case.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                          {estimate.case.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{estimate.case.project.name}</TableCell>
                  <TableCell>{getBillingTypeLabel(estimate.billing_type)}</TableCell>
                  <TableCell>{estimate.quantity}</TableCell>
                  <TableCell>${Number(estimate.rate).toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">
                      ${Number(estimate.total_amount).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>{estimate.created_by_user?.name || 'N/A'}</TableCell>
                  <TableCell>
                    {new Date(estimate.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleApprove(estimate)}
                        disabled={processing}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleRejectClick(estimate)}
                        disabled={processing}
                      >
                        Reject
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject Dialog */}
      <Dialog
        open={showRejectDialog}
        onClose={() => !processing && setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Estimate</DialogTitle>
        <DialogContent>
          {rejectingEstimate && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Case: <strong>{rejectingEstimate.case.title}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: <strong>${Number(rejectingEstimate.total_amount).toFixed(2)}</strong>
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Rejection Reason"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this estimate..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectSubmit}
            color="error"
            variant="contained"
            disabled={processing || !rejectionReason.trim()}
          >
            {processing ? 'Rejecting...' : 'Reject Estimate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FirmClientEstimates;

